import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useActiveSession } from '../api/active/sessionProvider';
import {
  createInventoryApi,
  InventoryApiError,
  type InventoryListQuery,
} from './inventoryApi';
import {
  type InventoryDeviceView,
  type InventoryPageView,
  type InventoryWarehouseView,
  mapInventoryPage,
  mapWarehouseList,
} from './inventoryMapper';

type ReadStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export type InventoryDeviceReadState = {
  status: Exclude<ReadStatus, 'idle'>;
  error: Error | null;
};

export type InventoryLoadOptions = {
  /**
   * 普通读取复用当前授权范围内已经读取过的设备；用户明确刷新时才强制
   * 重新读取。相同设备已有请求在途时，强制刷新仍合并到该请求。
   */
  force?: boolean;
};

const INVENTORY_DEVICE_BATCH_SIZE = 100;

export type InventoryReadRuntime = {
  canRead: boolean;
  warehouses: {
    status: ReadStatus;
    items: InventoryWarehouseView[];
    error: Error | null;
  };
  devices: {
    status: ReadStatus;
    byId: ReadonlyMap<string, InventoryDeviceView>;
    error: Error | null;
    /** 兼容旧消费者；新交互应按设备展示加载、空结果和失败。 */
    readStateById?: ReadonlyMap<string, InventoryDeviceReadState>;
  };
  page: {
    status: ReadStatus;
    value: InventoryPageView | null;
    error: Error | null;
  };
  loadWarehouses(options?: InventoryLoadOptions): Promise<void>;
  loadDevices(
    deviceIds: readonly string[],
    options?: InventoryLoadOptions,
  ): Promise<void>;
  loadDevice(deviceId: string, options?: InventoryLoadOptions): Promise<void>;
  loadPage(query: InventoryListQuery): Promise<void>;
};

const InventoryReadRuntimeContext = createContext<InventoryReadRuntime | null>(
  null,
);

export function InventoryReadRuntimeProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: InventoryReadRuntime;
}) {
  return (
    <InventoryReadRuntimeContext.Provider value={value}>
      {children}
    </InventoryReadRuntimeContext.Provider>
  );
}

export function ActiveInventoryReadRuntimeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { state, transport, refresh } = useActiveSession();
  const ready = state.status === 'ready' ? state : null;
  const readyAuthorizationScopeKey = ready
    ? JSON.stringify({
        companyId: ready.user.companyId,
        userId: ready.user.id,
        permissionVersion: ready.user.permissionVersion,
        roleViewId: ready.user.roleView?.roleId ?? null,
        roleViewPolicyVersion: ready.user.roleView?.policyVersion ?? null,
        permissionDataScopes: ready.user.permissionDataScopes ?? null,
        permissions: [...ready.user.permissions].sort(),
        dataScopes: ready.user.dataScopes
          .map((scope) => JSON.stringify(scope))
          .sort(),
        departments: [...ready.user.departments].sort(),
        primaryDepartmentId: ready.user.primaryDepartmentId,
      })
    : null;
  const canReadInventory =
    ready?.user.permissions.includes('inventory.read') ?? false;
  const activeAuthorizationScope = useRef(
    readyAuthorizationScopeKey ?? state.status,
  );
  // 会话续期会短暂进入 loading；同一身份续期不能误清除会话级库存。
  const authorizationScopeKey =
    readyAuthorizationScopeKey ??
    (state.status === 'loading'
      ? activeAuthorizationScope.current
      : state.status);
  const api = useMemo(() => createInventoryApi(transport), [transport]);
  const [warehouses, setWarehouses] = useState<
    InventoryReadRuntime['warehouses']
  >({ status: 'idle', items: [], error: null });
  const [devices, setDevices] = useState<InventoryReadRuntime['devices']>({
    status: 'idle',
    byId: new Map(),
    error: null,
    readStateById: new Map(),
  });
  const [page, setPage] = useState<InventoryReadRuntime['page']>({
    status: 'idle',
    value: null,
    error: null,
  });
  const warehouseController = useRef<AbortController | null>(null);
  const warehouseLoadedScope = useRef<string | null>(null);
  const warehouseInFlight = useRef<Promise<void> | null>(null);
  const pageController = useRef<AbortController | null>(null);
  const deviceControllers = useRef(new Set<AbortController>());
  const deviceValues = useRef(new Map<string, InventoryDeviceView>());
  const deviceReadStates = useRef(new Map<string, InventoryDeviceReadState>());
  const deviceInFlight = useRef(new Map<string, Promise<void>>());
  const latestDeviceRequest = useRef(new Map<string, number>());
  const deviceQueue = useRef<Promise<void>>(Promise.resolve());
  const deviceRequestSequence = useRef(0);
  const deviceScopeGeneration = useRef(0);
  const warehouseGeneration = useRef(0);
  const pageGeneration = useRef(0);

  const emptyDevices = useMemo<InventoryReadRuntime['devices']>(
    () => ({
      status: 'idle',
      byId: new Map(),
      error: null,
      readStateById: new Map(),
    }),
    [],
  );
  const emptyPage = useMemo<InventoryReadRuntime['page']>(
    () => ({ status: 'idle', value: null, error: null }),
    [],
  );

  const publishDevices = useCallback(() => {
    const byId = new Map(deviceValues.current);
    const readStateById = new Map(deviceReadStates.current);
    const firstError = [...readStateById.values()].find(
      (entry) => entry.status === 'error',
    )?.error;
    let status: ReadStatus = 'idle';
    // 已有可用记录时保持可展示，后台补读、刷新或部分失败不再清空页面。
    if (byId.size) status = 'ready';
    else if (
      [...readStateById.values()].some((entry) => entry.status === 'loading')
    )
      status = 'loading';
    else if (firstError) status = 'error';
    else if (readStateById.size) status = 'empty';
    setDevices({
      status,
      byId,
      error: firstError ?? null,
      readStateById,
    });
  }, []);

  useLayoutEffect(() => {
    if (activeAuthorizationScope.current === authorizationScopeKey) return;
    activeAuthorizationScope.current = authorizationScopeKey;
    deviceScopeGeneration.current += 1;
    for (const controller of deviceControllers.current) controller.abort();
    deviceControllers.current.clear();
    deviceValues.current.clear();
    deviceReadStates.current.clear();
    deviceInFlight.current.clear();
    latestDeviceRequest.current.clear();
    deviceQueue.current = Promise.resolve();
    warehouseController.current?.abort();
    warehouseLoadedScope.current = null;
    warehouseInFlight.current = null;
    pageController.current?.abort();
    warehouseGeneration.current += 1;
    pageGeneration.current += 1;
    setWarehouses({ status: 'idle', items: [], error: null });
    setPage({ status: 'idle', value: null, error: null });
    publishDevices();
  }, [authorizationScopeKey, publishDevices]);

  useEffect(
    () => () => {
      deviceScopeGeneration.current += 1;
      deviceInFlight.current.clear();
      latestDeviceRequest.current.clear();
      deviceQueue.current = Promise.resolve();
      warehouseController.current?.abort();
      for (const controller of deviceControllers.current) controller.abort();
      deviceControllers.current.clear();
      pageController.current?.abort();
    },
    [],
  );

  const authorized = useCallback(
    async <T,>(request: () => Promise<T>, retried = false): Promise<T> => {
      try {
        return await request();
      } catch (error) {
        if (
          !retried &&
          error instanceof InventoryApiError &&
          error.status === 401
        ) {
          await refresh();
          return authorized(request, true);
        }
        throw error;
      }
    },
    [refresh],
  );

  const mergeItems = useCallback(
    (items: readonly InventoryDeviceView[], requestSequence: number) => {
      for (const item of items) {
        if (
          (latestDeviceRequest.current.get(item.deviceId) ?? 0) >
          requestSequence
        )
          continue;
        latestDeviceRequest.current.set(item.deviceId, requestSequence);
        deviceValues.current.set(item.deviceId, item);
        deviceReadStates.current.set(item.deviceId, {
          status: 'ready',
          error: null,
        });
      }
      publishDevices();
    },
    [publishDevices],
  );

  const loadWarehouses = useCallback(
    async (options: InventoryLoadOptions = {}) => {
      if (!canReadInventory) return;
      if (
        !options.force &&
        warehouseLoadedScope.current === authorizationScopeKey
      )
        return;
      if (warehouseInFlight.current) return warehouseInFlight.current;
      warehouseController.current?.abort();
      const controller = new AbortController();
      const generation = ++warehouseGeneration.current;
      warehouseController.current = controller;
      setWarehouses((current) => ({
        ...current,
        status: 'loading',
        error: null,
      }));
      const operation = (async () => {
        try {
          const result = await authorized(() =>
            api.listWarehouses(controller.signal),
          );
          if (
            controller.signal.aborted ||
            generation !== warehouseGeneration.current
          )
            return;
          const items = mapWarehouseList(result);
          warehouseLoadedScope.current = authorizationScopeKey;
          setWarehouses({
            status: items.length ? 'ready' : 'empty',
            items,
            error: null,
          });
        } catch (error) {
          if (
            controller.signal.aborted ||
            generation !== warehouseGeneration.current
          )
            return;
          setWarehouses((current) => ({
            ...current,
            status: 'error',
            error:
              error instanceof Error ? error : new Error('仓库目录读取失败'),
          }));
        } finally {
          warehouseInFlight.current = null;
        }
      })();
      warehouseInFlight.current = operation;
      return operation;
    },
    [api, authorizationScopeKey, authorized, canReadInventory],
  );

  const loadDeviceQuery = useCallback(
    async (
      requestedDeviceIds: readonly string[],
      options: InventoryLoadOptions = {},
    ) => {
      if (!canReadInventory) return;
      const deviceIds = [...new Set(requestedDeviceIds.map((id) => id.trim()))]
        .filter(Boolean)
        .sort();
      if (!deviceIds.length) return;

      const sharedOperations = new Set<Promise<void>>();
      const pendingDeviceIds: string[] = [];
      for (const deviceId of deviceIds) {
        const inFlight = deviceInFlight.current.get(deviceId);
        if (inFlight) {
          sharedOperations.add(inFlight);
          continue;
        }
        const previous = deviceReadStates.current.get(deviceId);
        // A dependency outage is not a reusable inventory fact. Successful and
        // explicit empty reads remain session-cached, while a later normal read
        // is allowed to recover an earlier per-device error without requiring a
        // full page reload or a special force flag.
        if (!options.force && previous && previous.status !== 'error') continue;
        pendingDeviceIds.push(deviceId);
      }

      const scopeGeneration = deviceScopeGeneration.current;
      const scopeKey = authorizationScopeKey;
      for (
        let offset = 0;
        offset < pendingDeviceIds.length;
        offset += INVENTORY_DEVICE_BATCH_SIZE
      ) {
        const batch = pendingDeviceIds.slice(
          offset,
          offset + INVENTORY_DEVICE_BATCH_SIZE,
        );
        const requestSequence = ++deviceRequestSequence.current;
        for (const deviceId of batch) {
          latestDeviceRequest.current.set(deviceId, requestSequence);
          deviceReadStates.current.set(deviceId, {
            status: 'loading',
            error: null,
          });
        }
        publishDevices();

        const preceding = deviceQueue.current.catch(() => undefined);
        let operation: Promise<void>;
        operation = preceding
          .then(async () => {
            if (
              scopeGeneration !== deviceScopeGeneration.current ||
              scopeKey !== activeAuthorizationScope.current
            )
              return;
            const controller = new AbortController();
            deviceControllers.current.add(controller);
            try {
              const result = await authorized(() =>
                api.listInventory(
                  {
                    page: 1,
                    pageSize: batch.length,
                    deviceIds: batch,
                  },
                  controller.signal,
                ),
              );
              if (
                controller.signal.aborted ||
                scopeGeneration !== deviceScopeGeneration.current ||
                scopeKey !== activeAuthorizationScope.current
              )
                return;
              const items = mapInventoryPage(result).items;
              const itemById = new Map(
                items.map((item) => [item.deviceId, item] as const),
              );
              for (const deviceId of batch) {
                if (
                  latestDeviceRequest.current.get(deviceId) !== requestSequence
                )
                  continue;
                const item = itemById.get(deviceId);
                if (item) {
                  deviceValues.current.set(deviceId, item);
                  deviceReadStates.current.set(deviceId, {
                    status: 'ready',
                    error: null,
                  });
                } else {
                  deviceValues.current.delete(deviceId);
                  deviceReadStates.current.set(deviceId, {
                    status: 'empty',
                    error: null,
                  });
                }
              }
              publishDevices();
            } catch (error) {
              if (
                controller.signal.aborted ||
                scopeGeneration !== deviceScopeGeneration.current ||
                scopeKey !== activeAuthorizationScope.current
              )
                return;
              const normalizedError =
                error instanceof Error ? error : new Error('设备库存读取失败');
              for (const deviceId of batch) {
                if (
                  latestDeviceRequest.current.get(deviceId) !== requestSequence
                )
                  continue;
                deviceReadStates.current.set(deviceId, {
                  status: 'error',
                  error: normalizedError,
                });
              }
              publishDevices();
            } finally {
              deviceControllers.current.delete(controller);
            }
          })
          .finally(() => {
            for (const deviceId of batch) {
              if (deviceInFlight.current.get(deviceId) === operation)
                deviceInFlight.current.delete(deviceId);
            }
          });
        for (const deviceId of batch)
          deviceInFlight.current.set(deviceId, operation);
        deviceQueue.current = operation.catch(() => undefined);
        sharedOperations.add(operation);
      }
      await Promise.all([...sharedOperations]);
    },
    [api, authorizationScopeKey, authorized, canReadInventory, publishDevices],
  );

  const loadDevice = useCallback(
    (deviceId: string, options?: InventoryLoadOptions) =>
      loadDeviceQuery([deviceId], options),
    [loadDeviceQuery],
  );

  const loadPage = useCallback(
    async (query: InventoryListQuery) => {
      if (!canReadInventory) return;
      pageController.current?.abort();
      const controller = new AbortController();
      const generation = ++pageGeneration.current;
      const requestSequence = ++deviceRequestSequence.current;
      for (const deviceId of [
        ...(query.deviceId ?? []),
        ...(query.deviceIds ?? []),
      ])
        latestDeviceRequest.current.set(deviceId, requestSequence);
      pageController.current = controller;
      setPage((current) => ({ ...current, status: 'loading', error: null }));
      try {
        const result = await authorized(() =>
          api.listInventory(query, controller.signal),
        );
        if (controller.signal.aborted || generation !== pageGeneration.current)
          return;
        const value = mapInventoryPage(result);
        mergeItems(value.items, requestSequence);
        setPage({
          status: value.items.length ? 'ready' : 'empty',
          value,
          error: null,
        });
      } catch (error) {
        if (controller.signal.aborted || generation !== pageGeneration.current)
          return;
        setPage((current) => ({
          ...current,
          status: 'error',
          error: error instanceof Error ? error : new Error('库存列表读取失败'),
        }));
      }
    },
    [api, authorized, canReadInventory, mergeItems],
  );

  if (!ready) return null;
  const scopeMatches =
    activeAuthorizationScope.current === authorizationScopeKey;
  const value: InventoryReadRuntime = {
    canRead: canReadInventory,
    warehouses,
    devices: scopeMatches ? devices : emptyDevices,
    page: scopeMatches ? page : emptyPage,
    loadWarehouses,
    loadDevices: loadDeviceQuery,
    loadDevice,
    loadPage,
  };
  return (
    <InventoryReadRuntimeProvider value={value}>
      {children}
    </InventoryReadRuntimeProvider>
  );
}

export function useInventoryReadRuntime() {
  const value = useContext(InventoryReadRuntimeContext);
  if (!value) throw new Error('InventoryReadRuntimeProvider is required');
  return value;
}
